import React, { useEffect } from 'react';
import { 
  X, 
  ShoppingBag, 
  Trash2, 
  Plus, 
  Minus, 
  ArrowLeft, 
  ArrowRight, 
  Truck, 
  Sparkles, 
  ShieldCheck, 
  ChevronLeft 
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { formatPrice } from '../../utils/formatters';
import { getProductAltText } from '../../utils/seoHelpers';

const CartDrawer = () => {
  const { lang, t, isRtl } = useLanguage();
  const { settings } = useStore();
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    updateQuantity,
    removeFromCart,
    clearCart,
    subtotal,
    deliveryFee,
    totalAmount,
    isFreeDelivery,
    amountForFreeDelivery,
    freeDeliveryThreshold,
    setIsCheckoutOpen
  } = useCart();

  // Handle ESC key to close cart
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isCartOpen) {
        setIsCartOpen(false);
      }
    };
    if (isCartOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isCartOpen, setIsCartOpen]);

  if (!isCartOpen) return null;

  const handleProceedToCheckout = () => {
    setIsCartOpen(false);
    setIsCheckoutOpen(true);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden animate-fade-in">
      {/* Dark backdrop - click outside reliably closes cart */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity cursor-pointer"
        onClick={() => setIsCartOpen(false)}
        aria-label="Close cart backdrop"
      />

      {/* Drawer Container */}
      <div className={`fixed inset-y-0 ${isRtl ? 'right-0' : 'left-auto right-0'} max-w-full flex z-50 ${lang === 'ur' ? 'font-urdu' : 'font-sans'}`}>
        <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col justify-between h-full border-l border-gray-200">
          
          {/* Drawer Header with Prominent Close Button */}
          <div className="p-4 sm:p-5 border-b border-gray-100 flex items-center justify-between bg-pansar-900 text-white shadow-sm">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center border border-amber-500/30">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div className="text-start">
                <h3 className="font-bold text-base sm:text-lg font-title text-white">
                  {t('cart')} ({cartItems.reduce((a, b) => a + b.quantity, 0)})
                </h3>
                <span className="text-[11px] text-emerald-300">
                  {lang === 'ur' ? settings.storeNameUrdu : (settings.storeNameEn || 'Himalayan Pansar Store')}
                </span>
              </div>
            </div>

            {/* High-Contrast Unmissable Close Button */}
            <button
              type="button"
              onClick={() => setIsCartOpen(false)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white text-gray-900 hover:bg-rose-600 hover:text-white transition-all shadow-md font-bold text-xs border border-gray-200 cursor-pointer group"
              title={lang === 'ur' ? 'کارٹ بند کریں (Close)' : 'Close Cart'}
            >
              <X className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
              <span>{lang === 'ur' ? 'بند کریں' : 'Close'}</span>
            </button>
          </div>

          {/* Free Shipping Progress Indicator */}
          <div className="bg-emerald-50 px-4 py-2.5 border-b border-emerald-100">
            <div className="flex items-center gap-2 text-xs text-emerald-900 mb-1 text-start">
              <Truck className="w-4 h-4 text-emerald-600 shrink-0" />
              {isFreeDelivery ? (
                <span className="font-bold text-emerald-700">
                  {lang === 'ur' ? '🎉 مبارک ہو! آپ کا آرڈر مفت ڈلیوری کا اہل ہے۔' : '🎉 Congratulations! Your order qualifies for FREE Delivery.'}
                </span>
              ) : (
                <span>
                  {lang === 'ur' ? (
                    <>مفت ڈلیوری کے لیے مزید <strong className="text-emerald-800 font-sans">{formatPrice(amountForFreeDelivery, lang)}</strong> کی خریداری کریں۔</>
                  ) : (
                    <>Add <strong className="text-emerald-800 font-sans">{formatPrice(amountForFreeDelivery, lang)}</strong> more to get FREE Delivery.</>
                  )}
                </span>
              )}
            </div>
            {/* Progress bar */}
            <div className="w-full bg-emerald-200/70 h-1.5 rounded-full overflow-hidden">
              <div
                className="bg-emerald-600 h-full rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, (subtotal / freeDeliveryThreshold) * 100)}%` }}
              />
            </div>
          </div>

          {/* Cart Items List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {cartItems.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-gray-400 space-y-3">
                <ShoppingBag className="w-16 h-16 text-gray-200" />
                <p className="text-base text-gray-600">{t('cartEmpty')}</p>
                <button
                  type="button"
                  onClick={() => setIsCartOpen(false)}
                  className="px-6 py-3 rounded-2xl bg-pansar-800 text-white text-xs font-bold hover:bg-pansar-900 transition shadow-md cursor-pointer"
                >
                  {t('continueShopping')}
                </button>
              </div>
            ) : (
              cartItems.map((item) => (
                <div
                  key={item.cartItemId}
                  className="p-3 bg-gray-50 rounded-2xl border border-gray-100 flex items-center gap-3"
                >
                  <img
                    src={item.image}
                    alt={getProductAltText(item, 0, lang)}
                    className="w-16 h-16 rounded-xl object-cover border border-gray-200 shrink-0"
                  />
                  <div className="flex-1 min-w-0 text-start">
                    <h4 className="font-bold text-xs sm:text-sm text-gray-900 font-title truncate">
                      {lang === 'ur' ? item.nameUrdu : (item.nameEn || item.nameUrdu)}
                    </h4>
                    <span className="inline-block text-[11px] text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-100 mb-1">
                      {lang === 'ur' ? `وزن: ${item.variant?.weight}` : `Weight: ${item.variant?.weightEn || item.variant?.weight}`}
                    </span>
                    <div className="text-xs font-extrabold text-pansar-900 font-sans">
                      {formatPrice(item.unitPrice * item.quantity, lang)}
                    </div>
                  </div>

                  {/* Quantity Controls & Delete */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => removeFromCart(item.cartItemId)}
                      className="text-gray-400 hover:text-rose-600 p-1 transition cursor-pointer"
                      title={lang === 'ur' ? 'حذف کریں' : 'Remove'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg p-0.5 bg-white">
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                        className="p-1 hover:bg-gray-100 rounded text-gray-600 cursor-pointer"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-5 text-center text-xs font-bold font-sans">
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                        className="p-1 hover:bg-gray-100 rounded text-gray-600 cursor-pointer"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Drawer Footer & Actions */}
          {cartItems.length > 0 && (
            <div className="p-4 sm:p-5 border-t border-gray-100 bg-gray-50/90 space-y-3 text-start">
              <div className="space-y-1.5 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>{t('subtotal')}</span>
                  <span className="font-bold text-gray-900 font-sans">{formatPrice(subtotal, lang)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('deliveryFee')}</span>
                  <span className="font-bold text-gray-900 font-sans">
                    {deliveryFee === 0 ? (
                      <span className="text-emerald-700 font-bold">{t('freeDelivery')}</span>
                    ) : (
                      formatPrice(deliveryFee, lang)
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm sm:text-base font-extrabold text-pansar-950 border-t border-gray-200 pt-2">
                  <span className="font-title">{t('total')}</span>
                  <span className="font-sans text-pansar-900 font-black">{formatPrice(totalAmount, lang)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 pt-1">
                {/* 1. Proceed to full checkout (COD & Bank) */}
                <button
                  type="button"
                  onClick={handleProceedToCheckout}
                  className="w-full py-3.5 px-4 rounded-2xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-sm shadow-md flex items-center justify-center gap-2 font-title transition cursor-pointer"
                >
                  <span>{t('proceedToCheckout')}</span>
                  {isRtl ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                </button>

                {/* 3. Explicit Secondary Close / Continue Button */}
                <button
                  type="button"
                  onClick={() => setIsCartOpen(false)}
                  className="w-full py-2.5 px-4 rounded-xl bg-white hover:bg-gray-100 text-gray-700 font-bold text-xs flex items-center justify-center gap-1.5 transition border border-gray-200 cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>{lang === 'ur' ? 'خریداری جاری رکھیں (کارٹ بند کریں)' : 'Continue Shopping (Close Cart)'}</span>
                </button>
              </div>

              <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl py-2 px-3 text-center text-[11px] text-amber-900 flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-700 shrink-0" />
                {(settings.enableCod ?? true) ? (
                  settings.codAdvanceRequired ? (
                    <span>
                      {lang === 'ur' ? (
                        <><strong>پالیسی:</strong> کیش آن ڈلیوری پر {formatPrice(settings.codAdvanceAmount || 500, lang)} ایڈوانس تصدیق، بقیہ رقم پارسل وصولی پر ادا کریں۔</>
                      ) : (
                        <><strong>Policy:</strong> Rs. {formatPrice(settings.codAdvanceAmount || 500, lang)} advance confirmation on COD, pay remaining upon parcel delivery.</>
                      )}
                    </span>
                  ) : (
                    <span>{lang === 'ur' ? 'محفوظ خریداری اور کیش آن ڈلیوری کی ضمانت' : '100% Safe Shopping with Cash on Delivery Guarantee'}</span>
                  )
                ) : (
                  <span>{lang === 'ur' ? 'محفوظ آن لائن خریداری (ایزی پیسہ / بینک) اور فوری کوریئر ترسیل کی ضمانت' : 'Safe online shopping & fast courier dispatch guaranteed'}</span>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
